"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import {
  getSocialFeed,
  type SocialFeedCursor,
  type SocialFeedItem,
  type SocialFeedResponse,
} from "@/actions/social-feed";

export function useSocialFeed(
  limit: number,
  initialItems?: SocialFeedItem[],
  options?: { userId?: string; enabled?: boolean },
) {
  const initialData = initialItems
    ? {
        pages: [
          {
            items: initialItems,
            hasMore: false,
            nextCursor: null,
          } as SocialFeedResponse,
        ],
        pageParams: [null],
      }
    : undefined;

  return useInfiniteQuery({
    queryKey: options?.userId
      ? ["socialFeed", "profile", options.userId, limit]
      : ["socialFeed", "feed", limit],
    enabled: options?.enabled,
    queryFn: async ({ pageParam }) => {
      const result = await getSocialFeed({
        userId: options?.userId,
        cursor: (pageParam as number | SocialFeedCursor | null) ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch feed");
      }

      return result.data;
    },
    initialPageParam: null as number | SocialFeedCursor | null,
    initialData,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
    refetchOnMount: false,
    placeholderData: options?.userId ? undefined : keepPreviousData,
  });
}
